import { IBrand, IMarket } from 'data/schema';
import { IFeatureFlags, ITenantConfig, tenantConfigSchema } from 'data/tenant-config';
import { sendApiRequest } from './network';
import { userSession } from 'state/session';
import { State } from './state';
import { ApiError } from './errors';
import { isDebugMode } from './ui/debug';

export interface ITenantIdentifier {
  brand?: IBrand;
  market?: IMarket;
  tenant?: string;
}

export class TenantConfig extends State implements ITenantIdentifier {
  cache: Record<string, ITenantConfig>;
  brand!: IBrand;
  market!: IMarket;
  tenant!: string;
  isLoaded?: boolean = false;
  inactive = false;
  private isLoading = false;

  // Promise that resolves when the config is loaded
  // This allows us to wait for the config to be loaded before rendering the app
  configLoaded: Promise<unknown>;

  // Function to resolve the promise when the config is loaded
  loadingSuccess: (value: unknown) => void = () => {};

  // Function to resolve the promise when the config loading fails
  loadingFailed: (value: unknown) => void = () => {};

  constructor(identifier?: ITenantIdentifier, initialData?: ITenantConfig) {
    super();
    this.cache = {};
    this.configLoaded = new Promise((resolve, reject) => {
      this.loadingSuccess = resolve;
      this.loadingFailed = reject;
    });

    this.setIdentifier(identifier, initialData);

    if (!identifier) {
      // identifier is not forced so use the session
      userSession().on('change', (keys) => {
        if (['brand', 'market', 'partnerToken'].some((key) => keys.includes(key))) {
          // added debugging logs for https://collaboration.msi.audi.com/jira/browse/AVS-3817. Logs will be removed after the issue is resolved
          this.setIdentifier();
        }
      });
    }
  }

  // This is cache identifier. Change of any of these values will invalidate the cache
  // We allow a custom identifier to be passed in so we can use it on the backend
  setIdentifier(identifier: ITenantIdentifier = {}, initialData?: ITenantConfig) {
    this.brand = identifier.brand ?? (userSession().get('brand') as IBrand);
    this.market = identifier.market ?? (userSession().get('market') as IMarket);
    this.tenant = identifier.tenant ?? (userSession().get('partnerToken') as string);

    if (initialData) {
      this.setConfig(initialData);
      return;
    }

    if (!this.brand || !this.market) {
      return;
    }

    this.loadConfig();
  }

  setConfig(config?: ITenantConfig) {
    if (config) {
      this.cache[this.generateCacheKey()] = config;
    }
    this.emit('change', 'config', config);
  }

  get config() {
    const cacheKey = this.generateCacheKey();
    const cached = this.cache[cacheKey];
    if (cached) {
      return cached;
    }

    return null;
  }

  // Load config from the server
  private async loadConfig() {
    const cacheKey = this.generateCacheKey();
    const cached = this.cache[cacheKey];
    if (cached) {
      this.isLoaded = true;
      return cached;
    }

    if (this.isLoading) {
      await this.configLoaded;
      return this.cache[cacheKey];
    }

    this.isLoading = true;

    try {
      const data = tenantConfigSchema.parse(await this.fetchConfig());
      this.inactive = !data || data.enabled === false;
      this.isLoaded = true;
      this.setConfig(data!);

      if (data) {
        this.loadingSuccess(data);

        if (!this.isAllowed('voip')) {
          userSession().setValues({ customerAllowed: true });
        }
      }
      return data;
    } finally {
      this.isLoading = false;
    }
  }

  // Fetch feature flags from the server for a specific brand and market
  private async fetchConfig() {
    try {
      const data = await sendApiRequest<ITenantConfig>(`/config?brand=${this.brand}&market=${this.market}`, {
        method: 'GET',
      });

      return data;
    } catch (error) {
      // Handle abort errors gracefully - they're expected when requests are cancelled
      if (error instanceof ApiError && error.message.includes('aborted')) {
        console.error('Config request was cancelled:', error.message);
        return null;
      }
      console.error('Failed to fetch feature flags:', error);
      this.loadingFailed(error);
      return null;
    }
  }

  // Get a specific feature flag
  isAllowed(flag: keyof IFeatureFlags) {
    const config = this.config;
    if (!config) {
      return false;
    }

    return config.flags[flag] ?? false;
  }

  // Private helper to generate cache keys
  private generateCacheKey() {
    return `${this.brand}_${this.market}_${this.tenant}`;
  }
}

let state: TenantConfig;

// Initialize the tenant config once, just when needed
export function tenantConfig() {
  if (!state) {
    state = new TenantConfig();
  }
  return state;
}

if (isDebugMode()) {
  // @ts-expect-error - expose config in global scope for debugging
  window.tenantConfig = tenantConfig;
}
